import React, { useEffect, useState } from 'react';
import { FaPencilAlt, FaCheck } from 'react-icons/fa';
import { IoClose } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import apiClient from '../apiClient';
import './Profile.css';

export default function Profile() {
  const [user, setUser] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({});
  const [success, setSuccess] = useState('');
  const [pwForm, setPwForm] = useState({ password: '', confirm: '' });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      // First get user data from localStorage as fallback
      const localUserData = JSON.parse(localStorage.getItem('ezocmUser') || '{}');
      
      // Set initial data from localStorage
      if (localUserData && Object.keys(localUserData).length > 0) {
        setUser(localUserData);
        setForm(localUserData);
      }

      // Then try to get updated user data from backend
      const response = await apiClient.get('/auth/me');
      const userData = response.data;
      
      setUser(userData);
      setForm(userData);
      
      // Update localStorage with fresh data
      localStorage.setItem('ezocmUser', JSON.stringify(userData));
    } catch (error) {
      console.error('Failed to load user data from API:', error);
      // Keep using localStorage data if API fails
      const fallbackData = JSON.parse(localStorage.getItem('ezocmUser') || '{}');
      if (fallbackData && Object.keys(fallbackData).length > 0) {
        setUser(fallbackData);
        setForm(fallbackData);
      } else {
        setUser({});
        setForm({});
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => setEditMode(true);
  const handleCancel = () => { setEditMode(false); setForm(user); };
  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });
  
  const handleSave = async (e) => {
    e.preventDefault();
    
    try {
      const response = await apiClient.put('/auth/me', form);
      
      // Update state with response data
      setUser(response.data);
      localStorage.setItem('ezocmUser', JSON.stringify(response.data));
      
      setEditMode(false);
      setSuccess('Profile updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Failed to update profile via API:', error);
      // Fallback to localStorage update
      setUser(form);
      localStorage.setItem('ezocmUser', JSON.stringify(form));
      setEditMode(false);
      setSuccess('Profile updated (locally)!');
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const handlePwChange = e => setPwForm({ ...pwForm, [e.target.name]: e.target.value });
  
  const handlePwSave = e => {
    e.preventDefault();
    setPwError('');
    setPwSuccess('');
    if (pwForm.password.length < 8) {
      setPwError('Password must be at least 8 characters.');
      return;
    }
    if (pwForm.password !== pwForm.confirm) {
      setPwError('Passwords do not match.');
      return;
    }
    setPwSuccess('Password changed successfully!');
    setPwForm({ password: '', confirm: '' });
    setTimeout(() => setPwSuccess(''), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-lg text-gray-600">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center bg-gray-50 py-12 px-4">
      {/* Profile Information */}
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-2xl mb-8 relative">
        {/* Close Button */}
        <button
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-2xl z-10"
          onClick={() => {
            // Navigate based on user role
            if (user.role === 'Team Member') {
              navigate('/my-assessments');
            } else {
              navigate('/reports');
            }
          }}
          aria-label="Close profile"
        >
          <IoClose />
        </button>
        
        <div className="flex items-center justify-between mb-6 pr-10">
          <div className="flex gap-2 items-center">
            {!editMode && (
              <button onClick={handleEdit} className="text-blue-700 hover:text-blue-900 p-2 rounded-full" aria-label="Edit Profile">
                <FaPencilAlt size={20} />
              </button>
            )}
            {editMode && (
              <>
                <button type="button" onClick={handleCancel} className="text-gray-400 hover:text-gray-700 p-2 rounded-full" aria-label="Cancel Edit">
                  <IoClose size={22} />
                </button>
                <button type="button" onClick={handleSave} className="text-green-600 hover:text-green-800 p-2 rounded-full" aria-label="Save Profile">
                  <FaCheck size={20} />
                </button>
              </>
            )}
          </div>
        </div>
        
        {success && <div className="mb-4 p-2 bg-green-100 text-green-700 rounded">{success}</div>}
        
        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {['name','email','organization','role','phone','title','department'].map(field => (
              <div key={field}>
                <label className="block text-gray-500 text-sm mb-1 capitalize">{field.replace(/([A-Z])/g, ' $1')}</label>
                <input
                  type="text"
                  name={field}
                  value={form[field] || ''}
                  onChange={handleChange}
                  disabled={!editMode}
                  className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
            ))}
          </div>
        </form>
      </div>

      {/* Simplified Subscription Section - Only for Change Managers and Admins */}
      {(user.role === 'Change Manager' || user.role === 'Admin') && (
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-2xl mb-8">
          <h3 className="text-xl font-semibold text-blue-900 mb-4">Subscription</h3>
          <div className="text-center py-4">
            <p className="text-gray-600 mb-2">
              Current Plan: <strong className="text-blue-900">{user.plan?.name || 'Free'}</strong>
            </p>
            {user.subscription?.status === 'trial' && (
              <p className="text-sm text-amber-600 mb-4">
                Trial expires in {user.subscription?.trial_days_remaining || 0} days
              </p>
            )}
            <button
              onClick={() => navigate('/settings/subscription')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Manage Subscription
            </button>
          </div>
        </div>
      )}

      {/* Change Password Section */}
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-2xl mb-8">
        <h3 className="text-xl font-semibold text-blue-900 mb-4">Change Password</h3>
        <form onSubmit={handlePwSave} className="space-y-4 max-w-md">
          <div>
            <label className="block text-gray-500 text-sm mb-1">New Password</label>
            <input
              type="password"
              name="password"
              value={pwForm.password}
              onChange={handlePwChange}
              className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              minLength={8}
              required
            />
          </div>
          <div>
            <label className="block text-gray-500 text-sm mb-1">Confirm Password</label>
            <input
              type="password"
              name="confirm"
              value={pwForm.confirm}
              onChange={handlePwChange}
              className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              minLength={8}
              required
            />
          </div>
          {pwError && <div className="text-red-600 text-sm">{pwError}</div>}
          {pwSuccess && <div className="text-green-600 text-sm">{pwSuccess}</div>}
          <button type="submit" className="px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors">Change Password</button>
        </form>
      </div>
    </div>
  );
}